import {
  Circle,
  CircleDashed,
  CircleDot,
  CircleCheck,
  CheckCircle2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { TaskColumnId } from './types'

export type TaskColumnConfig = {
  id: TaskColumnId
  label: string
  /** Status written through to CES when a card is dropped in this column */
  primaryStatus: string
  /** All statuses that map into this column on read */
  statuses: string[]
  /** Icon in the column header pill */
  icon: LucideIcon
  /** Tailwind class for the header pill background */
  pillBgClass: string
  /** Tailwind class for the header pill text color */
  pillTextClass: string
  /** Tailwind class for the column body background tint */
  columnBgClass: string
  emptyCopy: string
}

/**
 * Unified Kanban columns for the CES-backed task queue.
 */
export const TASK_COLUMNS: TaskColumnConfig[] = [
  {
    id: 'backlog',
    label: 'Backlog',
    primaryStatus: 'OPEN',
    statuses: ['OPEN'],
    icon: CircleDashed,
    pillBgClass: 'bg-status-gray-xsubtle',
    pillTextClass: 'text-muted-foreground',
    columnBgClass: 'bg-card',
    emptyCopy: 'Parked tests appear here',
  },
  {
    id: 'todo',
    label: 'Todo',
    primaryStatus: 'ASSIGNED',
    statuses: ['ASSIGNED'],
    icon: Circle,
    pillBgClass: 'bg-card border border-input',
    pillTextClass: 'text-text-body',
    columnBgClass: 'bg-card',
    emptyCopy: 'No issues',
  },
  {
    id: 'in_progress',
    label: 'In Progress',
    primaryStatus: 'IN_PROGRESS',
    statuses: ['IN_PROGRESS'],
    icon: CircleDot,
    pillBgClass: 'bg-[#E8A317]',
    pillTextClass: 'text-white',
    columnBgClass: 'bg-status-orange-xsubtle',
    emptyCopy: 'No issues',
  },
  {
    id: 'in_review',
    label: 'In Review',
    primaryStatus: 'IN_REVIEW',
    statuses: ['IN_REVIEW'],
    icon: CircleCheck,
    pillBgClass: 'bg-status-green',
    pillTextClass: 'text-white',
    columnBgClass: 'bg-status-green-xsubtle',
    emptyCopy: 'No issues',
  },
  {
    id: 'done',
    label: 'Done',
    primaryStatus: 'RESOLVED',
    statuses: ['RESOLVED', 'DISMISSED'],
    icon: CheckCircle2,
    pillBgClass: 'bg-status-blue',
    pillTextClass: 'text-white',
    columnBgClass: 'bg-status-blue-xsubtle',
    emptyCopy: 'No issues',
  },
]

export const ACTIVE_TASK_STATUSES = TASK_COLUMNS.filter(
  (column) => column.id !== 'done'
).flatMap((column) => column.statuses)

const STATUS_TO_COLUMN: Map<string, TaskColumnId> = new Map(
  TASK_COLUMNS.flatMap((col) => col.statuses.map((s) => [s, col.id] as const))
)

export function columnForStatus(status: string): TaskColumnId {
  return STATUS_TO_COLUMN.get(status) ?? 'backlog'
}

export function primaryStatusForColumn(columnId: TaskColumnId): string {
  const col = TASK_COLUMNS.find((c) => c.id === columnId)
  return col?.primaryStatus ?? 'OPEN'
}

export function configForColumn(columnId: TaskColumnId): TaskColumnConfig {
  return TASK_COLUMNS.find((c) => c.id === columnId) ?? TASK_COLUMNS[0]
}

export function configForStatus(status: string): TaskColumnConfig {
  return configForColumn(columnForStatus(status))
}

export function isDoneStatus(status: string): boolean {
  return columnForStatus(status) === 'done'
}
